import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import RouteLoading from 'Components/RouteLoading';
import { shouldShowExecutionOrder } from 'Selectors';
import errorController from 'Components/ErrorScreens';
import _ from 'lodash';
import EditForm from 'Components/EditRulePage/EditForm';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useUpdateGlobalRules from 'Hooks/query-hooks/global-rules/useUpdateGlobalRules';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import { useQueryParam } from 'Hooks/useQueryParam';
import { getRulePriority } from 'Hooks/query-hooks/helper';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import useGetList from 'Hooks/query-hooks/list/useGetList';
import { FormikValues } from 'formik';

const UpdateTemplatedRulePage = () => {
    const { ruleId } = useParams<{ ruleId: string }>();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const showExecutionOrder = shouldShowExecutionOrder();
    const { users } = useGetReduxUsers();
    const globalRulesAPI = useGlobalRules({ users, showDeletedRule: true });
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const listAPI = useGetList();
    const { hasError, errorCmp } = errorController([globalRulesAPI.error, parametersAPI.error, listAPI.error]);
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const history = useHistory();
    const formRef = React.useRef<FormikValues>();
    const {
        parsed: { selectedRowId }
    } = useQueryParam();
    const globalMutation = useUpdateGlobalRules(templateRepositoryId!, {
        mode: 'rule',
        ruleId,
        revision: globalRulesAPI.data?.revision,
        mutationOptions: {
            onSuccess: (_data, variables) => {
                addAlert({
                    id: 'update-rule',
                    content: t('updateRuleSuccess', { ruleId: variables?.rules?.[0]?.id }),
                    status: 'success'
                });
                history.push(`/global-rules?selectedRowId=${selectedRowId}`);
            },
            onError: (_data, variables) => {
                addAlert({
                    id: 'update-rule',
                    content: t('updateRuleError', { ruleId: variables?.rules?.[0]?.id }),
                    status: 'danger'
                });
                if (formRef.current) {
                    formRef.current.resetForm();
                }
            }
        }
    });

    React.useEffect(() => {
        // always scroll to the top
        window.scrollTo(0, 0);
    }, []);

    if (
        listAPI.isLoading ||
        listAPI.isFetching ||
        globalRulesAPI.isLoading ||
        globalRulesAPI.isFetching ||
        parametersAPI.isLoading ||
        parametersAPI.isFetching
    ) {
        return <RouteLoading />;
    }
    if (hasError) return <>{errorCmp}</>;

    if (!globalRulesAPI.data || _.isEmpty(globalRulesAPI.data)) return <span>{t('There are no rules')}</span>;
    if (showExecutionOrder) {
        globalRulesAPI.data.rules.forEach((rule) => {
            if (!rule.priority) {
                rule.priority = getRulePriority(t, rule);
            }
        });
    }

    const globalRule = globalRulesAPI.data?.rules.find((i: Rule) => i.id === ruleId);
    if (!globalRule) return <span>{t('This rule does not exist')}</span>;

    return (
        <EditForm
            globalRule={globalRule}
            parameterData={parametersAPI.data}
            listData={listAPI.data}
            rulesType='globalRules'
            ruleId={ruleId}
            pageHeader={t('Edit Rule With Rule ID', { ruleId })}
            mutation={globalMutation}
            selectedRowId={selectedRowId?.toString()}
            formRef={formRef}
        />
    );
};

export default UpdateTemplatedRulePage;
